import queryString from "query-string";
import { useCallback, useEffect, useState } from "react";
import { type RouteComponentProps } from "react-router";

import { type UserInfo } from "helpers/AppProvider.types";

import { BTLoginTypes } from "types/enum";

import { parseParams } from "utilities/url/url";

import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import { marketplaceAdminDialog } from "commonComponents/utilities/ModalLauncher/MarketplaceAdminDialog";
import { marketplaceNonBuilderDialog } from "commonComponents/utilities/ModalLauncher/MarketplaceNonBuilderDialog";

interface IModalLauncherProps extends RouteComponentProps {
    search: string;
    userInfo?: UserInfo;
}

const ModalLauncher: React.FC<IModalLauncherProps> = ({ search, userInfo }) => {
    const [qs] = useState(() =>
        parseParams<{ openModalUrl: string | string[]; checkAccess: boolean }>(search, {
            parseBooleans: true,
        }),
    );
    const [modalUrl, setModalUrl] = useState<string>();

    const removeQsParams = useCallback(() => {
        delete qs.openModalUrl;
        delete qs.checkAccess;
        let newQs = queryString.stringify(qs);

        if (newQs !== "") {
            newQs = "?" + newQs;
        }

        history.pushState(null, document.title, location.pathname + newQs);
    }, [qs]);

    const openModal = useCallback(
        (openModalUrl: string | string[]) => {
            // this is in-case there's more than one modal in the url
            const encodedUrl = Array.isArray(openModalUrl)
                ? openModalUrl[openModalUrl.length - 1]
                : openModalUrl;

            const url = decodeURIComponent(encodedUrl);

            if (url.length > 0 && url.startsWith("/app/")) {
                removeQsParams();
                setModalUrl(queryString.stringifyUrl({ url, query: { isModal: "true" } }));
            }
        },
        [removeQsParams],
    );

    const showNoAccessModal = useCallback(
        (userInfo: UserInfo) => {
            removeQsParams();
            userInfo.loginType === BTLoginTypes.BUILDER
                ? marketplaceAdminDialog()
                : marketplaceNonBuilderDialog();
        },
        [removeQsParams],
    );

    useEffect(() => {
        const { openModalUrl, checkAccess } = qs;

        if (checkAccess && userInfo && !userInfo.isAdmin) {
            showNoAccessModal(userInfo);
        } else if (openModalUrl) {
            openModal(openModalUrl);
        }
    }, [qs, userInfo, showNoAccessModal, openModal]);

    if (modalUrl === undefined) {
        return null;
    }

    return (
        <BTModal
            visible
            url={modalUrl}
            width="1200px"
            iframeHeight="600px"
            beforeClose={() => setModalUrl(undefined)}
        />
    );
};

export default ModalLauncher;
